'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React from 'react';

import BirthdayAtTheCinemaForm from '@/app/[locale]/(default-layout)/(forms)/_components/BirthdayAtTheCinemaForm';
import { getFormContact } from '@/app/[locale]/(default-layout)/(forms)/_components/form-contact-helper';
import {
    resolveFormPageText,
    resolveFormPageTitle,
    resolveFormImageUrl
} from '@/app/[locale]/(default-layout)/(forms)/_components/form-image-helper';
import FormContactLinks from '@/app/[locale]/(default-layout)/(forms)/_components/FormContactLinks';

import { ID_INNENSTADT_KINOS } from '@cineamo/api/constants/cinema-ids';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import CinemaDescriptionMarkdown from '@cineamo/ui/src/markdown/CinemaDescriptionMarkdown';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const BirthdayImage = loadAsset('/images/placeholders/im-cinemaScreen.png');
const BirthdayAtTheCinemaImageInnenstadtKinos = 'https://cdn.cineamo.com/tmp/cinema-778-innenstadtkinos/birthday_2.jpg';

type BirthdayAtTheCinemaPageClientProps = {
    /** Fallback hero image (first cinema room picture), used when no image is configured. */
    hallImageUrl?: string;
};

const BulletPoint: React.FC = () => {
    return (
        <div className="size-[16px] rounded-full flex items-center justify-center flex-shrink-0 outline-none bg-[var(--primary-color)]">
            <div className="size-[6px] rounded-full bg-[var(--on-primary-color)]" />
        </div>
    );
};

const BirthdayAtTheCinemaPageClient: React.FC<BirthdayAtTheCinemaPageClientProps> = ({ hallImageUrl }) => {
    const t = useTranslations();
    const isMounted = useIsMounted();
    const domainConfiguration = useDomainConfiguration();

    const { phone, phoneURI, email } = getFormContact(domainConfiguration, 'birthdayAtTheCinema');

    const isCinemaInnenstadtkinos = domainConfiguration.cinema?.id === ID_INNENSTADT_KINOS;

    // Custom image configured on the cinema app's "our services" forms section takes
    // precedence, then the cinema-specific hardcoded override below, then the hall picture.
    const heroImage =
        resolveFormImageUrl(domainConfiguration, 'birthdayAtTheCinema', hallImageUrl) ||
        (isCinemaInnenstadtkinos ? BirthdayAtTheCinemaImageInnenstadtKinos : undefined) ||
        BirthdayImage;

    const customDescription = resolveFormPageText(domainConfiguration, 'birthdayAtTheCinema');
    const customPageTitle = resolveFormPageTitle(domainConfiguration, 'birthdayAtTheCinema');

    const bulletPointData = [
        { label: t('birthday-at-the-cinema.info.age-range') },
        {
            label: t('birthday-at-the-cinema.info.min-guests')
        },
        {
            label: t('birthday-at-the-cinema.info.time-restriction')
        },
        {
            label: t('birthday-at-the-cinema.info.age-rating')
        },
        isCinemaInnenstadtkinos
            ? {
                  label: t('birthday-at-the-cinema.info.price')
              }
            : null
    ].filter((it) => !!it);

    return (
        <main className="min-h-screen">
            {/* Main Content */}
            <section className="py-24">
                <CenterLayout>
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Left Column - Content */}
                        <div className="space-y-12">
                            <div className="space-y-6">
                                <h2 className="text-headline-32 text-[var(--on-background-color)]">
                                    {customPageTitle || t('birthday-at-the-cinema.hero.title')}
                                </h2>
                                <div className="text-body-16 text-[rgba(var(--on-background-color-rgb),0.8)]">
                                    {customDescription ? (
                                        <CinemaDescriptionMarkdown
                                            description={customDescription}
                                            allowInlineHtml
                                        />
                                    ) : (
                                        t('birthday-at-the-cinema.hero.description')
                                    )}
                                </div>
                            </div>

                            {/* The info list and the note below it are default copy, so a custom page text
                                replaces them too rather than sitting above boilerplate. */}
                            {!customDescription && (
                                <>
                                    {/* Info List */}
                                    <div className="space-y-6">
                                        <h3 className="text-headline-20 font-semibold text-[var(--on-background-color)]">
                                            {t('birthday-at-the-cinema.info.title')}
                                        </h3>
                                        <ul className="space-y-4">
                                            {bulletPointData.map((data, index) => (
                                                <li
                                                    key={`BulletPoint#${index}`}
                                                    className="flex items-center space-x-3">
                                                    <BulletPoint />
                                                    <span className="text-body-16 text-[var(--on-background-color)]">
                                                        {data.label}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Additional Info Text */}
                                    <div
                                        className={cn(
                                            'p-4 rounded-8 border-l-4',
                                            'bg-[var(--primary-color)]/10',
                                            'border-l-[var(--primary-color)]'
                                        )}>
                                        <p className="text-body-14 leading-relaxed text-[var(--on-background-color)]">
                                            <strong>{t('birthday-at-the-cinema.info.note')}</strong>{' '}
                                            {t('birthday-at-the-cinema.info.program-schedule-info')}
                                            <br />
                                            <br />
                                            {t('birthday-at-the-cinema.info.online-booking-info')}
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Right Column - Image */}
                        <div className="relative aspect-[16/9] rounded-12 overflow-hidden">
                            {isMounted && (
                                <Image
                                    src={heroImage}
                                    alt="Birthday at the Cinema"
                                    fill
                                    className="object-cover"
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                />
                            )}
                        </div>
                    </div>
                </CenterLayout>
            </section>

            {/* Registration Form Section */}
            <section className="py-24">
                <CenterLayout>
                    <div className="max-w-2xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-headline-32 text-[var(--on-background-color)]">
                                {t('birthday-at-the-cinema.form.title')}
                            </h2>
                        </div>

                        <BirthdayAtTheCinemaForm />
                    </div>
                </CenterLayout>
            </section>

            {/* Contact Section */}
            <section className="mt-48 py-24 backdrop-blur-sm bg-[rgba(var(--on-background-color-rgb),0.05)]">
                <CenterLayout>
                    <div className="text-center space-y-8">
                        <h2 className="text-headline-32 text-[var(--on-background-color)]">
                            {t('birthday-at-the-cinema.contact.question')}
                        </h2>
                        <p className="text-body-16 max-w-2xl mx-auto text-[rgba(var(--on-background-color-rgb),0.8)]">
                            {t('birthday-at-the-cinema.contact.help-text')}
                        </p>
                        <FormContactLinks
                            namespace="birthday-at-the-cinema"
                            phone={phone}
                            phoneURI={phoneURI}
                            email={email}
                        />
                    </div>
                </CenterLayout>
            </section>
        </main>
    );
};

export default BirthdayAtTheCinemaPageClient;
